import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { KeyRound, Loader2, AlertCircle, CheckCircle2, Link2 } from 'lucide-react'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'

interface Credential {
  id: number
  clientId: string
  environment: string
}

interface PropertyResult {
  shortname: string
  name: string
  currency: string
  status: string
  already_onboarded: boolean
  error?: string
}

interface CredentialFormProps {
  credentials: Credential[]
  // Whether to offer the Eviivo Environment selector. False on the real production
  // deployment: a hotelier's Eviivo account is always production, and a wrong pick fails
  // auth against the QA host — three of those suspend a live connection. Presentation only;
  // the backend coerces to production regardless (EviivoCredential.effective_environment).
  allowStagingEnvironment: boolean
  onSuccess: (data: { credentialId: number; properties: PropertyResult[] }) => void
}

type FormMode = 'new' | 'existing'


export default function CredentialForm({ credentials, allowStagingEnvironment, onSuccess }: CredentialFormProps) {
  const hasExisting = credentials.length > 0
  const [mode, setMode] = useState<FormMode>(hasExisting ? 'existing' : 'new')

  // New credential fields
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [environment, setEnvironment] = useState('production')

  // Existing credential selection
  const [selectedCredentialId, setSelectedCredentialId] = useState<string>(
    hasExisting ? String(credentials[0].id) : ''
  )

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmitNew = clientId.trim() !== '' && clientSecret.trim() !== ''
  const canSubmitExisting = selectedCredentialId !== ''
  const canSubmit = mode === 'new' ? canSubmitNew : canSubmitExisting

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const body: Record<string, string> =
        mode === 'new'
          ? { client_id: clientId.trim(), client_secret: clientSecret, environment }
          : { credential_id: selectedCredentialId }

      const response = await apiFetch('/onboarding/validate_credentials', {
        method: 'POST',
        body: JSON.stringify(body),
      })

      const data = await response.json()

      if (!response.ok) {
        const messages = data.errors?.join(', ') || 'Validation failed. Please check your credentials.'
        setError(messages)
        return
      }

      onSuccess({ credentialId: data.credentialId, properties: data.properties })
    } catch (e) {
      setError(apiErrorMessage(e, 'Network error. Please check your connection and try again.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="animate-fade-in-up">
      <div className="rounded-xl border border-border/60 bg-white p-6 shadow-sm">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Link2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Connect your PMS</h2>
            <p className="text-sm text-muted-foreground">
              Enter the API credentials from your Eviivo account to link your properties
            </p>
          </div>
        </div>

        {/* Mode toggle (only show when existing credentials exist) */}
        {hasExisting && (
          <div className="mb-6 inline-flex h-10 items-center rounded-md bg-muted p-1 text-muted-foreground">
            <button
              type="button"
              onClick={() => setMode('existing')}
              className={cn(
                'inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium transition-all',
                mode === 'existing'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Existing Credentials
            </button>
            <button
              type="button"
              onClick={() => setMode('new')}
              className={cn(
                'inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium transition-all',
                mode === 'new'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              New Credentials
            </button>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mb-6 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 animate-fade-in-up">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {mode === 'existing' ? (
            /* Existing credential selector */
            <div className="space-y-2">
              <Label htmlFor="credential-select">Saved Credential</Label>
              <Select value={selectedCredentialId} onValueChange={setSelectedCredentialId}>
                <SelectTrigger id="credential-select">
                  <SelectValue placeholder="Select a credential..." />
                </SelectTrigger>
                <SelectContent>
                  {credentials.map((cred) => (
                    <SelectItem key={cred.id} value={String(cred.id)}>
                      <div className="flex items-center gap-2">
                        <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-mono text-xs">{cred.clientId.substring(0, 12)}...</span>
                        <span className={cn(
                          'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                          cred.environment === 'production'
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'bg-amber-50 text-amber-600'
                        )}>
                          {cred.environment}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            /* New credential form */
            <>
              <div className="space-y-2">
                <Label htmlFor="client-id">Client ID</Label>
                <Input
                  id="client-id"
                  type="text"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="font-mono text-sm"
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client-secret">Client Secret</Label>
                <Input
                  id="client-secret"
                  type="password"
                  placeholder="Enter your client secret"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  className="font-mono text-sm"
                  autoComplete="off"
                />
              </div>

              {/* Eviivo Environment. Hidden on the real production deployment: every hotelier's
                  Eviivo account is production, and a wrong pick authenticates against the QA host
                  — three failures suspend a live connection. The backend coerces regardless. */}
              {allowStagingEnvironment && (
                <div className="space-y-2">
                  <Label htmlFor="environment">Environment</Label>
                  <Select value={environment} onValueChange={setEnvironment}>
                    <SelectTrigger id="environment">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="production">Production</SelectItem>
                      <SelectItem value="staging">Staging</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          <div className="pt-2">
            <Button type="submit" disabled={!canSubmit || loading} className="w-full rounded-lg">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Validate & Connect
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
